import { OptionType } from 'components/UI/SelectField';
import { APIError } from 'models';

export type Software = {
  id: string;
  type: string;
  attributes: {
    pnc: string;
    version: string;
    name: string;
    revision: number;
    category: string;
    createdAt: number;
    mimetype: string;
    brand: string;
    purposes: string[];
    bundleId: string;
    firmwareVersion: string;
    releaseStage: string;
    releasedFor?: {
      productId: string;
      releaseDate: number;
      pnc: string;
    }[];
    file: {
      id: string;
      fileName: string;
      preSignedUrl?: string;
    };
  };
};
export type SoftwareUploadAttributes = {
  name: string;
  version: string;
  revision: number;
  category: string;
  firmwareVersion: string;
  bundleId: string;
  mimetype: string;
  releaseStage: string;
  releasedFor: string;
  purposes: string[];
  pnc: string;
  file: {
    id: string;
    fileName: string;
  };
};
export type SoftwareFileUpload = {
  id: string;
  type: string;
  attributes: {
    url: string;
  };
  preSignedUrl: string;
};

export type ReleaseSoftwareRequest = {
  data: {
    id: string;
    type: string;
    attributes: {
      pnc: string;
      releaseDate: number;
    };
  };
};

export type UpdateSoftwareRequest = {
  data: {
    id: string;
    type: string;
    attributes: {
      releaseStage: string;
    };
  };
};
export type SoftwareList = {
  data: Software[] | null;
  totalCount: number;
  isLoading: boolean;
  error: APIError | null;
  filters: {
    active: boolean;
    data: SoftwareFilter[] | null;
  };
};
export type SoftwareUploadSuccess = {
  data: Software | null;
  isLoading: boolean;
  error: APIError | null;
};
export type SoftwareDetailsSuccess = {
  data: Software | null;
  isLoading: boolean;
  isUpdatedSuccessfully: boolean;
  error: APIError | null;
};
export enum SoftwareFilterAttribute {
  NAME = 'name',
  VERSION = 'version',
  PURPOSE = 'purpose',
  PNC = 'pnc',
  BRAND = 'brand',
  CATEGORY = 'category',
  RELEASED_FOR = 'released_for',
  BUNDLE_ID = 'bundle_id',
}
export enum SoftwareAttributes {
  NAME = 'name',
  VERSION = 'version',
  PURPOSE = 'purpose',
  PNC = 'pnc',
  BRAND = 'brand',
  CATEGORY = 'category',
  RELEASED_FOR = 'released_for',
  RELEASE_STAGE = 'releaseStage',
}

export enum SoftwareReleaseAttribute {
  PNC = 'pnc',
  RELEASE_DATE = 'releaseDate',
}

export enum SoftwareCategoryAttribute {
  CATEGORY = 'category',
  ROBOTICS = 'ROBOTICS',
  WHEELED = 'WHEELED',
  CONSTRUCTION = 'CONSTRUCTION',
  HANDHELD = 'HANDHELD',
  ACCESSORIES = 'ACCESSORIES',
  WATERING = 'WATERING',
  BATTERY_TESTER = 'BATTERY_TESTER',
}

export enum SoftwarePurposeAttribute {
  PURPOSE = 'purpose',
  AFTERSALE_SERVICE = 'AFTERSALE_SERVICE',
  ASSEMBLY = 'ASSEMBLY',
  FOTA = 'FOTA',
  MANUFACTURING = 'MANUFACTURING',
}

export enum SoftwareReleaseStageAttribute {
  RELEASE_STAGE = 'releaseStage',
  RELEASED = 'RELEASED',
  INTERNAL_TEST = 'INTERNAL_TEST',
  EXTERNAL_TEST = 'EXTERNAL_TEST',
  INVALIDATED = 'INVALIDATED',
}

export const releaseStageOptions: OptionType[] = [
  {
    key: 'RELEASED',
    label: 'RELEASED',
    value: SoftwareReleaseStageAttribute.RELEASED,
  },
  {
    key: 'EXTERNAL_TEST',
    label: 'EXTERNAL_TEST',
    value: SoftwareReleaseStageAttribute.EXTERNAL_TEST,
  },
  {
    key: 'INTERNAL_TEST',
    label: 'INTERNAL_TEST',
    value: SoftwareReleaseStageAttribute.INTERNAL_TEST,
  },
  {
    key: 'INVALIDATED',
    label: 'INVALIDATED',
    value: SoftwareReleaseStageAttribute.INVALIDATED,
  },
];

export enum SoftwareMimeTypeAttribute {
  MIME_TYPE = 'mimeType',
  APPLICATION_BIN = 'application/bin',
  HEX = 'hex',
  MTO = 'mto',
  SWU = 'swu',
  ZIP = 'zip',
}

export type SoftwareFilter = {
  attribute: SoftwareFilterAttribute;
  value: string;
};

export type SoftwareState = {
  list: SoftwareList;
  uploadSoftware: SoftwareUploadSuccess;
  softwareDetail: SoftwareDetailsSuccess;
};

export type SoftwareSuccess = {
  response: Software[];
  totalCount: number;
  filters: SoftwareFilter[] | null;
};

export interface SoftwaresResponse {
  data: Software[];
  meta: SoftwareMeta;
  errors?: APIError[];
}

export type SoftwareMeta = {
  totalCount: number;
};

export type SoftwareFilterObject = {
  name: string;
  pnc: string | number;
};

export enum SoftwarePurpose {
  FOTA = 'FOTA',
  AFTERSALE_SERVICE = 'AFTERSALE_SERVICE',
  MANUFACTURING = 'MANUFACTURING',
  ASSEMBLY = 'ASSEMBLY',
}
